import { Button, Menu, MenuItem, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { treatmentApi } from 'src/api/treatment';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import { visitApi } from 'src/api/visit';
import Iconify from 'src/components/iconify';
import type { FilesUploadModalFormValuesProps } from 'src/components/files-upload-modal';
import type { MedicationsFormModalValuesProps } from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import type { RemindersFormSubmitValues } from 'src/sections/@dashboard/profile/components/ProfileRemindersFormModal';
import type { TreatmentFormSubmitValues } from 'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal';
import type { TreatmentFormSnapshot } from 'src/sections/@dashboard/profile/components/ProfileTreatments/treatmentFormValues';
import {
  facilityMedicationBody,
  useMedicationCreateSeries,
} from 'src/sections/@dashboard/visit/hooks/useMedicationWriteSeries';
import { useTreatmentCreateSeries } from 'src/sections/@dashboard/visit/hooks/useTreatmentWriteSeries';
import { ClinicalWriteRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import WarehouseSaleEntry from 'src/sections/@dashboard/warehouse/components/sales/WarehouseSaleEntry';
import { AddProfileTreatmentDto, CreateReminderDto } from 'woofwoof-api';
import { useVisitModalAction } from './VisitModalActionContext';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { translateKey } from 'src/locales/translate';
import PatientNoteModal from 'src/sections/@dashboard/profile/components/PatientNoteModal/PatientNoteModal';

// Lazy chunks — pre-warmed on mount via useEffect below.
const MedicationFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal'
    ),
);
const ProfileTreatmentFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal'
    ),
);
const ProfileRemindersFormModal = lazy(
  () =>
    import(
      'src/sections/@dashboard/profile/components/ProfileRemindersFormModal'
    ),
);
const FilesUploadModal = lazy(() => import('src/components/files-upload-modal'));

type ActiveAddModal =
  | 'medication'
  | 'treatment'
  | 'reminder'
  | 'document'
  | 'note'
  | 'sale'
  | null;

interface VisitAddMenuProps {
  visitId: string;
  patientId: string;
  organizationId?: string;
  canAddVisitData: boolean;
  canAddPatientNotes: boolean;
  canAddInternalNotes: boolean;
  // Any member of the visit's organization may sell: no edit window, any vet.
  canAddSale: boolean;
  // Hide the footer button entirely (e.g. while editing notes, where the
  // footer already shows the save/cancel pair).
  hidden?: boolean;
  onActivityChange: () => void;
  onAddPatientNotes: () => void;
  onAddInternalNotes: () => void;
  // Fired after a successful add so the parent can switch to the matching tab
  // (mirrors how the notes items jump to their tab).
  onAdded: (kind: 'medication' | 'treatment' | 'reminder' | 'document') => void;
}

/**
 * The visit modal's global "Dodaj do wizyty" action: a single always-visible
 * footer button (placed via the `addAction` context slot, so it persists across
 * every detail tab) whose menu can add medications, treatments, reminders and
 * documents — and jump straight into editing patient / internal notes. The add
 * forms (and their mutations) live here, lifted out of the Przebieg-wizyty tab
 * so they no longer depend on that tab being mounted.
 */
export default function VisitAddMenu({
  visitId,
  patientId,
  organizationId,
  canAddVisitData,
  canAddPatientNotes,
  canAddInternalNotes,
  canAddSale,
  hidden,
  onActivityChange,
  onAddPatientNotes,
  onAddInternalNotes,
  onAdded,
}: VisitAddMenuProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const writeError = useStockWriteError();
  const { isProfessionalRoleSelected } = useOrganizationContext();
  const { setAddAction, summaryAvailable, triggerSummary } =
    useVisitModalAction();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveAddModal>(null);

  // The pre-Sale rights. "Podsumowanie wizyty" stays behind them: the Sale
  // opens the menu on read-only visits, but must not widen the summary's reach.
  const canAddVisitContent =
    canAddVisitData || canAddPatientNotes || canAddInternalNotes;
  const canShowButton = !hidden && (canAddVisitContent || canAddSale);

  // Pre-warm lazy modal chunks so the first "Dodaj …" click opens instantly.
  useEffect(() => {
    if (!canAddVisitData) return;
    const preload = () => {
      import(
        'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal'
      );
      import(
        'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal'
      );
      import(
        'src/sections/@dashboard/profile/components/ProfileRemindersFormModal'
      );
      import('src/components/files-upload-modal');
    };
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void) => number;
        cancelIdleCallback?: (handle: number) => void;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') {
      const handle = ric(preload);
      return () => {
        (
          window as unknown as { cancelIdleCallback?: (handle: number) => void }
        ).cancelIdleCallback?.(handle);
      };
    }
    const timer = setTimeout(preload, 500);
    return () => clearTimeout(timer);
  }, [canAddVisitData]);

  // Treatment catalog for the "Dodaj zabieg" form — belongs to the organization.
  const { data: catalogData } = useQuery(
    ['treatment-catalog-add-menu', organizationId],
    () =>
      treatmentApi.treatmentControllerGetCatalog({
        organizationId: organizationId!,
      }),
    { enabled: canAddVisitData && Boolean(organizationId) },
  );

  const handleCloseModal = () => setActiveModal(null);

  const handleOpenAdd = (type: ActiveAddModal) => {
    setActiveModal(type);
    setMenuAnchor(null);
  };

  // All writes here go through the visit's own endpoints. The server
  // resolves the profile link from visit.profileId (null for guest visits),
  // persists visitId on the entity, logs activity and regenerates the relevant
  // AI summary — so there's no guest/authed branch to keep on the FE.
  // R3-4: write series through the visit's own route, keyed by the visit's
  // patient: the same create series as the course card and patient card.
  const visitRoute: ClinicalWriteRoute = {
    via: 'visit',
    profileId: patientId || null,
    visitId,
  };
  const medicationSeries = useMedicationCreateSeries({
    route: visitRoute,
    onSuccess: () => {
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
      handleCloseModal();
      onAdded('medication');
    },
    onError: writeError(t('visit.visitCourseAddError')),
  });

  const treatmentSeries = useTreatmentCreateSeries({
    route: visitRoute,
    onSuccess: () => {
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
      handleCloseModal();
      onAdded('treatment');
    },
    onError: writeError(t('visit.visitCourseAddError')),
  });

  const { mutate: addReminder, isLoading: isAddingReminder } = useMutation({
    mutationFn: (payload: CreateReminderDto) =>
      visitScopedPrescribingApi.addReminder(visitId, payload),
    onSuccess: () => {
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
      handleCloseModal();
      onAdded('reminder');
    },
  });

  const { mutateAsync: uploadDocuments, isLoading: isUploadingDocument } =
    useMutation({
      mutationFn: (files: FilesUploadModalFormValuesProps['files']) =>
        visitScopedPrescribingApi.addDocuments(visitId, files || []),
      onSuccess: () => {
        onActivityChange();
        enqueueSnackbar(t('visit.visitCourseAddSuccess'), {
          variant: 'success',
        });
        handleCloseModal();
        onAdded('document');
      },
    });

  const { mutateAsync: createTextNote, isLoading: isCreatingNote } = useMutation({
    mutationFn: (content: string) =>
      visitApi.visitControllerCreateVisitTextNote({
        visitId,
        createTextNoteDto: { content },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['patient-documents-list', patientId]);
      queryClient.invalidateQueries(['visit-activity', visitId]);
      enqueueSnackbar(translateKey('hardcoded.notatka_zostala_dodana'), {
        variant: 'success',
      });
      handleCloseModal();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          translateKey('hardcoded.wystapil_blad_podczas_zapisywania_notatki'),
        { variant: 'error' },
      );
    },
  });

  const handleMedicationSubmit = (data: MedicationsFormModalValuesProps) => {
    medicationSeries.submit(null, facilityMedicationBody(data), data);
  };

  const handleTreatmentSubmit = (
    values: TreatmentFormSubmitValues,
    shown: TreatmentFormSnapshot,
  ) => {
    treatmentSeries.submit(
      null,
      {
        ...values,
        ...(organizationId ? { organizationId } : {}),
      } as AddProfileTreatmentDto,
      shown,
    );
  };

  const handleReminderSubmit = (values: RemindersFormSubmitValues) => {
    addReminder({
      ...values,
      // Guest visits have no profile (patientId is ''); omit profileId so the
      // server keys the reminder by visitId only. Sending '' fails @IsMongoId.
      ...(patientId ? { profileId: patientId } : {}),
      ...(organizationId ? { organizationId } : {}),
      visitId,
    } as CreateReminderDto);
  };

  // Publish the "Dodaj do wizyty" button into the footer's dedicated add slot,
  // so it stays visible regardless of which detail tab is active.
  useEffect(() => {
    if (!canShowButton) {
      setAddAction(null);
      return;
    }
    setAddAction(
      <Button
        size="medium"
        variant="contained"
        startIcon={<Iconify icon="eva:plus-fill" width={16} />}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{
          background: theme.palette.common.black,
          color: theme.palette.common.white,
          textTransform: 'none',
        }}
      >
        {t('visit.visitCourseAddToVisit')}
      </Button>,
    );
    return () => setAddAction(null);
  }, [canShowButton, t, setAddAction, theme]);

  const handleSelectPatientNotes = () => {
    setMenuAnchor(null);
    onAddPatientNotes();
  };

  const handleSelectInternalNotes = () => {
    setMenuAnchor(null);
    onAddInternalNotes();
  };

  return (
    <>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {canAddPatientNotes && (
          <MenuItem onClick={handleSelectPatientNotes}>
            {t('visit.visitCourseMenuPatientNotes')}
          </MenuItem>
        )}
        {canAddVisitData && (
          <MenuItem onClick={() => handleOpenAdd('medication')}>
            {t('visit.visitCourseMenuMedication')}
          </MenuItem>
        )}
        {canAddVisitData && (
          <MenuItem onClick={() => handleOpenAdd('treatment')}>
            {t('visit.visitCourseMenuTreatment')}
          </MenuItem>
        )}
        {canAddVisitData && (
          <MenuItem onClick={() => handleOpenAdd('reminder')}>
            {t('visit.visitCourseMenuReminder')}
          </MenuItem>
        )}
        {canAddVisitData && (
          <MenuItem onClick={() => handleOpenAdd('document')}>
            {t('visit.visitCourseMenuDocument')}
          </MenuItem>
        )}
        {canAddVisitData && (
          <MenuItem onClick={() => handleOpenAdd('note')}>
            {translateKey('hardcoded.notatka')}
          </MenuItem>
        )}
        {canAddSale && (
          <MenuItem onClick={() => handleOpenAdd('sale')}>
            {t('warehouse.sales.visitMenuItem')}
          </MenuItem>
        )}
        {canAddInternalNotes && (
          <MenuItem onClick={handleSelectInternalNotes}>
            {t('visit.visitCourseMenuInternalNotes')}
          </MenuItem>
        )}
        {summaryAvailable && canAddVisitContent && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              triggerSummary();
            }}
          >
            {t('visit.visitCourseMenuSummary')}
          </MenuItem>
        )}
      </Menu>

      <Suspense fallback={null}>
        {activeModal === 'medication' && (
          <MedicationFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            onSubmit={handleMedicationSubmit}
            isCreating={medicationSeries.isLoading}
            isUpdating={false}
            singleReminderOnly={!patientId}
            stockEnabled
            patientId={patientId || undefined}
            unconfirmedSave={medicationSeries.pending}
            unconfirmedBlocked={medicationSeries.blocked}
            unconfirmedValues={medicationSeries.frozenValues}
            stockWriteError={medicationSeries.lastError}
          />
        )}
        {activeModal === 'treatment' && (
          <ProfileTreatmentFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            onSubmit={handleTreatmentSubmit}
            onDelete={() => {}}
            stockEnabled
            unconfirmedSave={treatmentSeries.pending}
            unconfirmedBlocked={treatmentSeries.blocked}
            unconfirmedValues={treatmentSeries.frozenValues}
            isProfessional
            organizationId={organizationId}
            catalogOptions={catalogData?.data ?? []}
            isCreating={treatmentSeries.isLoading}
            isUpdating={false}
            isDeleting={false}
            singleReminderOnly={!patientId}
          />
        )}
        {activeModal === 'reminder' && (
          <ProfileRemindersFormModal
            isModalOpen={true}
            closeModal={handleCloseModal}
            onSubmit={handleReminderSubmit}
            onDelete={() => {}}
            isCreating={isAddingReminder}
            isUpdating={false}
            isDeleting={false}
            singleReminderOnly={!patientId}
          />
        )}
        {activeModal === 'document' && (
          <FilesUploadModal
            isOpen={true}
            onClose={handleCloseModal}
            onUpload={async (files) => {
              if (files) await uploadDocuments(files);
            }}
            isLoading={isUploadingDocument}
            profileId={patientId}
            visitId={visitId}
          />
        )}
      </Suspense>
      {canAddSale && organizationId && (
        <WarehouseSaleEntry
          open={activeModal === 'sale'}
          organizationId={organizationId}
          visitId={visitId}
          onClose={handleCloseModal}
        />
      )}
      <PatientNoteModal
        isOpen={activeModal === 'note'}
        onClose={handleCloseModal}
        mode="create"
        profileId={patientId}
        onSubmit={async (content) => {
          await createTextNote(content);
        }}
        isSaving={isCreatingNote}
        canUseAssistant={false}
        enableDictation={!!isProfessionalRoleSelected}
      />
    </>
  );
}
